/* eslint-disable max-lines */
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Icon } from '@fuxi/eevee-icon';
import { nanoid } from '@reduxjs/toolkit';
import { useSelector } from 'react-redux';
import { Button, Dialog } from 'antd-mobile';
import { useMemoizedFn, useMount, useUnmountedRef } from 'ahooks';
import { Loading3QuartersOutlined } from '@ant-design/icons';
import service from '@/service';
import { selectCurrentServingOrder } from '@/store/service';
import { SocketClientType } from '@/service/socketio';
import type { RefAttrs as Body2DViewerRef } from '@/components/body-2d-viewer';
import loadingImg from '@/assets/image/loading.png';
import { When } from '@/components';
import { padArrayToLength, Toast } from '@/utils';
import { storagePrefixFixedV2 } from '@/constant';
import { MassageControlValueContext, PalpationEventBus } from '../../index';
import { GUIDE_VIDEO_POSTER } from '../index';
import DrawLineSettingBox, { DrawLineSettingBoxRefAttrs } from './draw-line-setting-box';

import cx from './index.module.scss';

interface Props {
  body2DViewerRef: React.MutableRefObject<Body2DViewerRef>;
  socketInstance: React.MutableRefObject<SocketClientType>;
  isImageLoaded: boolean;
  isSocketConnected: boolean;
  customRouteList: GetPalpationDatResponse['palpationData']['custom_routes'];
  setCustomRouteList: React.Dispatch<React.SetStateAction<Props['customRouteList']>>;
  onSuccessPalpation: VoidFunc;
}
const ROUTE_GUIDE_VIDEO = storagePrefixFixedV2 + '/videos/h264/route-guide.mp4';

export const CUSTOM_LINE_COLOR: Record<Massage.CustomLineState, string> = {
  Detected: '#2E73FF',
  Generated: '#41DB62',
  Error: '#DB3056',
};

const ROUTE_ERROR_MASG: Record<Massage.CustomLineErrorState, string> = {
  OutOfRange: '超出画线范围',
  DetectionTimeout: '检测超时',
  RouteTooLong: '手势画线过长',
};

const CustomDrawLine: React.FC<Props> = ({
  body2DViewerRef,
  socketInstance,
  customRouteList,
  setCustomRouteList,
  onSuccessPalpation,
  isSocketConnected,
}) => {
  const currentServingOrder = useSelector(selectCurrentServingOrder);
  const { massageState } = useContext(MassageControlValueContext);
  // const [customRouteList, setCustomRouteList] = useState<GetPalpationDatResponse['palpationData']['custom_routes']>([
  //   // {
  //   //   id: 'aasaadas',
  //   //   visual_coordinates: [164, 215, 319, 161, 325, 161, 280, 300],
  //   //   tech_coordinates: [10, 20, 30, 40, 30, 20, 100, 60],
  //   //   state: 'Generated', // 状态
  //   //   error: null, // 错误信息
  //   //   tech_model_coordinates: [],
  //   // },
  // ]);
  /** 当前是否正在画线 */
  const [isDrawingCustomLine, setIsDrawingCustomLine] = useState(false);
  const [isClickingPrevStep, setIsClickingPrevStep] = useState(false);
  const [isConfirmingDrawLine, setIsConfirmingDrawLine] = useState(false);
  const [isConfirmingSettingAndStart, setIsConfirmingSettingAndStart] = useState(false);
  const [isReDrawingLine, setIsReDrawingLine] = useState(false);
  const [isInitedSettingData, setIsInitedSettingData] = useState(false);
  const drawLineSettingBoxRef = useRef<DrawLineSettingBoxRefAttrs>(null);
  const unmountedRef = useUnmountedRef();

  const isConfirmBtnDisabled = useMemo(
    () => !customRouteList?.length || customRouteList?.some(item => item.state === 'Error'),
    [customRouteList]
  );

  // 当前是否正在生成线路
  const isGeneratingRoute = useMemo(() => massageState === 'Preparing_WaitingTechRouteGenerating', [massageState]);

  // 根据服务端状态判断,自定义画线轨迹是否生成完成
  const isCustomRouteGenerated = useMemo(
    () =>
      !!customRouteList?.length &&
      customRouteList?.every(item => item.state === 'Generated') &&
      massageState !== 'Preparing_Setting',
    [customRouteList, massageState]
  );

  const handleDeleteLine = useMemoizedFn(async (item: GetPalpationDatResponse['palpationData']['custom_routes'][0]) => {
    // 异常的线路不需要二次确认
    if (item.state !== 'Error') {
      Dialog.confirm({
        title: '确定删除该理疗线路吗？',
        content: '该操作将无法撤销',
        confirmText: '确认',
        async onConfirm() {
          await socketInstance.current.deleteCustomRoutes({ route_ids: [item.id] });
          setCustomRouteList(prev => prev?.filter(item2 => item2.id !== item.id));
        },
      });
    } else {
      await socketInstance.current.deleteCustomRoutes({ route_ids: [item.id] });
      setCustomRouteList(prev => prev?.filter(item2 => item2.id !== item.id));
    }
  });

  const handleReDrawLine = useMemoizedFn(async () => {
    try {
      setIsReDrawingLine(true);
      // 如果有一条是非异常的线，二次确认
      if (customRouteList?.some(item => item.state !== 'Error')) {
        Dialog.confirm({
          title: '确认要重新画线吗？',
          content: (
            <div>
              确认后将直接删除已经画线
              <br />
              的理疗线路
            </div>
          ),
          confirmText: '确认',
          async onConfirm() {
            await socketInstance.current.deleteCustomRoutes({
              route_ids: customRouteList?.map(item => item.id),
            });
            setCustomRouteList([]);
          },
        });
      } else {
        await socketInstance.current.deleteCustomRoutes({
          route_ids: customRouteList?.map(item => item.id),
        });
        setCustomRouteList([]);
      }
    } finally {
      setIsReDrawingLine(false);
    }
  });

  const handleConfirmSettingAndStart = useMemoizedFn(async () => {
    try {
      setIsConfirmingSettingAndStart(true);
      const settingValue = drawLineSettingBoxRef.current.getValues();

      await service.massage.startCustomRouteTask({
        serviceOrderId: currentServingOrder?.orderId,
        shockwaveSettings: JSON.stringify(settingValue),
      });
      onSuccessPalpation?.();
    } finally {
      setIsConfirmingSettingAndStart(false);
    }
  });

  const handleClickPreStep = useMemoizedFn(async () => {
    try {
      setIsClickingPrevStep(true);
      await socketInstance.current.continueToMarkNodules();
    } finally {
      setIsClickingPrevStep(false);
    }
  });

  const handleConfirm = useMemoizedFn(async () => {
    try {
      setIsConfirmingDrawLine(true);

      // 已完成生成手法线，执行确认操作
      if (isCustomRouteGenerated) {
        await socketInstance.current.confirmGeneratedCustomRoutes();
      } else {
        await socketInstance.current.generateCustomRoutes();
      }
    } finally {
      setIsConfirmingDrawLine(false);
    }
  });

  const initData = useMemoizedFn(async () => {
    const palpationData = await socketInstance.current.getPalpationData();
    setCustomRouteList(palpationData?.palpation_data?.custom_routes || []);
  });

  const initListener = useMemoizedFn(() => {
    if (unmountedRef.current) return;
    PalpationEventBus.on('custom_route_drawing_start', () => {
      setIsDrawingCustomLine(true);
    });

    PalpationEventBus.on('custom_route_detected', async () => {
      const palpationData = await socketInstance.current.getPalpationData();
      setCustomRouteList(palpationData?.palpation_data?.custom_routes || []);
      setIsDrawingCustomLine(false);
    });

    PalpationEventBus.on('custom_route_max_quantity_exceeded', () => {
      Toast.showWithEmphasis('已超过画线数量', { duration: 5000 });
      setIsDrawingCustomLine(false);
    });

    PalpationEventBus.on('custom_route_detect_timeout', () => {
      Toast.showWithAlarm('画线超时，请重新画线', { duration: 5000 });
      setIsDrawingCustomLine(false);
    });

    PalpationEventBus.on('custom_route_detected_multiple_hands', () => {
      Toast.showWithHand('请保证理疗区域仅用单只手画线', { duration: 5000 });
      setIsDrawingCustomLine(false);
    });

    PalpationEventBus.on('custom_route_generated', async () => {
      const palpationData = await socketInstance.current.getPalpationData();
      setCustomRouteList(palpationData?.palpation_data?.custom_routes || []);
      setIsDrawingCustomLine(false);
    });
  });

  useMount(() => {
    initData();
  });

  useEffect(() => {
    if (isSocketConnected) {
      initListener();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSocketConnected]);

  return (
    <div className={cx('CustomDrawLine')}>
      <div className={cx('draw-line')}>
        <When condition={massageState !== 'Preparing_Setting'}>
          <div className={cx('draw-line-header', { highlight: customRouteList?.length === 4 })}>
            <Icon name="draw-line" className={cx('icon')} />
            <div>手势画线｜{customRouteList?.length === 4 ? '已画满4条理疗线路' : '最多可画4条线路'}</div>
          </div>
        </When>
        <div className={cx('draw-line-content', { disabled: isGeneratingRoute })}>
          {massageState === 'Preparing_WaitingDrawCustomRoute' ? (
            <div className={cx('prepare-waiting-draw')}>
              <div className={cx('tips')}>
                <div className={cx('left')}>
                  <Icon name="info-circle" color="#000000A6" className={cx('icon')} size={18} />
                </div>
                <div className={cx('right')}>
                  <div className={cx('main-info')}>请将食指和中指并拢，并贴近人体表面进行画线</div>
                  <div className={cx('sub-info')}>请确认顾客趴好，身体保持相对静止状态</div>
                </div>
              </div>
              <div className={cx('tips-img')}>
                <video src={ROUTE_GUIDE_VIDEO} autoPlay loop muted poster={GUIDE_VIDEO_POSTER} />
              </div>
            </div>
          ) : ['Preparing_DrawCustomRoute', 'Preparing_WaitingTechRouteGenerating'].includes(massageState) ? (
            <div className={cx('line-info-content')}>
              {padArrayToLength(customRouteList, 4).map((item, index) => (
                <div className={cx('draw-line-item')} key={nanoid(5)}>
                  {item?.id ? (
                    <div
                      className={cx('line-info')}
                      onClick={() => {
                        body2DViewerRef.current?.animateDottedLine(item.id);
                      }}
                    >
                      <Icon
                        name="close-drawer"
                        className={cx('del-icon')}
                        onClick={e => {
                          e.stopPropagation();
                          handleDeleteLine(item);
                        }}
                      />
                      <div className={cx('line-text')}>线路</div>
                      <div
                        className={cx('line-item-num', {
                          error: item.state === 'Error',
                          generated: item.state === 'Generated',
                        })}
                      >
                        {index + 1}
                      </div>
                      {item.error && <div className={cx('err-info')}>{ROUTE_ERROR_MASG[item.error]}</div>}
                    </div>
                  ) : isDrawingCustomLine && customRouteList?.length === index ? (
                    <div className={cx('is-drawing')}>
                      <img src={loadingImg} alt="" className={cx('loading-icon')} />
                      <div>正在画线中</div>
                    </div>
                  ) : (
                    <div className={cx('empty-filler')}>理疗线路</div>
                  )}
                </div>
              ))}
            </div>
          ) : massageState === 'Preparing_Setting' ? (
            <DrawLineSettingBox
              ref={drawLineSettingBoxRef}
              socketInstance={socketInstance}
              onInitOK={() => setIsInitedSettingData(true)}
            />
          ) : null}
          <When condition={customRouteList?.length && isCustomRouteGenerated}>
            <div className={cx('line-notice')}>
              <div className={cx('header')}>
                <Icon name="warning" className={cx('icon')} />
                <div className={cx('main-text')}>画线算法计算轨迹注意事项</div>
                <div className={cx('sub-text')}>* 由于智能安全机制，实际执行路线为绿色路径</div>
              </div>
              <div className={cx('content')}>
                <div className={cx('notice-item')}>
                  <div className={cx('dot')} />
                  <span>
                    <span className={cx('highlight', 'blue')}>蓝色线段</span>
                    <span>为您手势画线线路</span>
                  </span>
                </div>
                <div className={cx('notice-item')}>
                  <div className={cx('dot', 'green')} />
                  <span>
                    <span className={cx('highlight', 'green')}>绿色线段</span>
                    <span>为机械臂规划运行路径</span>
                  </span>
                </div>
                <div className={cx('notice-item')}>
                  <div className={cx('dot', 'red')} />
                  <span>
                    <span className={cx('highlight', 'red')}>红色线段</span>
                    <span>为无法成功运行的线路，需要重绘</span>
                  </span>
                </div>
              </div>
            </div>
          </When>
        </div>
        <div
          className={cx('draw-line-footer', {
            disabled: isGeneratingRoute,
            'gray-bg': massageState === 'Preparing_Setting',
          })}
        >
          {massageState === 'Preparing_Setting' ? (
            <div className={cx('setting-footer')}>
              <Button loading={isClickingPrevStep} className={cx('redraw-btn')} onClick={handleClickPreStep}>
                上一步
              </Button>
              <Button
                color="primary"
                loading={isConfirmingSettingAndStart}
                disabled={!isInitedSettingData || isConfirmBtnDisabled}
                className={cx('confirm-btn')}
                onClick={handleConfirmSettingAndStart}
              >
                确认并开始
              </Button>
            </div>
          ) : massageState === 'Preparing_WaitingDrawCustomRoute' ? (
            <Button color="primary" loading={isConfirmingDrawLine} disabled={true} className={cx('confirm-btn')}>
              确认画线
            </Button>
          ) : (
            <>
              <When condition={!!customRouteList?.length}>
                <Button
                  className={cx('redraw-btn')}
                  disabled={isDrawingCustomLine}
                  loading={isReDrawingLine}
                  onClick={handleReDrawLine}
                >
                  一键重画
                </Button>
              </When>
              <Button
                color="primary"
                loading={isConfirmingDrawLine}
                disabled={isConfirmBtnDisabled || isDrawingCustomLine}
                className={cx('confirm-btn')}
                onClick={handleConfirm}
              >
                {isCustomRouteGenerated ? '确认并继续' : '确认画线'}
              </Button>
            </>
          )}
        </div>
        {isGeneratingRoute && (
          <div className={cx('generating-info')}>
            <Loading3QuartersOutlined spin className={cx('icon')} />
            <div>自定义线路生成中...</div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CustomDrawLine;
